import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { useExperimental } from "../context/ExperimentalFeaturesContext";
import { FEATURE_AGENCY_INVOICING } from "../experimentalFeatures";

interface SearchResult {
  type: string;
  label: string;
  path: string;
  score: number;
  id?: string;
  sublabel?: string;
  preview?: string | null;
  status?: string;
}

const NAVIGATION_SHORTCUTS = [
  { label: "Dashboard", keywords: ["dashboard", "home", "main"], path: "/", roles: null },
  { label: "Calendar", keywords: ["calendar", "schedule", "dates"], path: "/calendar", roles: null },
  { label: "Officers", keywords: ["officers", "cops", "police", "personnel", "staff", "badge"], path: "/officers", roles: ["admin"] },
  { label: "Manage Jobs", keywords: ["jobs", "manage jobs", "job list", "postings", "shifts"], path: "/jobs-hub/manage", roles: ["admin", "secretary"] },
  { label: "Post a Job", keywords: ["post", "post job", "create job", "new job", "add job"], path: "/jobs-hub/post", roles: ["admin", "secretary"] },
  { label: "Job Requests", keywords: ["requests", "job requests", "pending requests"], path: "/jobs-hub/requests", roles: ["admin", "secretary"] },
  { label: "Invoices", keywords: ["invoices", "invoice", "billing", "payment", "payments"], path: "/jobs-hub/invoices", roles: ["admin", "secretary"], feature: FEATURE_AGENCY_INVOICING },
  { label: "Rate Tiers", keywords: ["rate", "rates", "tiers", "pay", "pay rate"], path: "/jobs-hub/rate-tiers", roles: ["admin"] },
  { label: "Reminder Settings", keywords: ["reminders", "reminder", "notifications", "settings"], path: "/jobs-hub/reminders", roles: ["admin"] },
  { label: "Organizations", keywords: ["organizations", "entities", "companies", "org", "orgs", "clients"], path: "/entities-hub/manage", roles: ["admin"] },
  { label: "Entity Invites", keywords: ["invites", "invite", "entity invite"], path: "/entities-hub/invites", roles: ["admin"] },
  { label: "Audit Log", keywords: ["audit", "audit log", "history", "logs", "activity"], path: "/audit-hub", roles: ["admin"] },
  { label: "My Audit Log", keywords: ["audit", "my audit", "history", "logs", "activity"], path: "/audit", roles: ["officer"] },
  { label: "My Profile", keywords: ["profile", "my profile", "account", "settings", "edit profile"], path: "/profile", roles: null },
];

const MAX_RESULTS_PER_CATEGORY = 5;
const RECENT_SEARCHES_KEY = "ood_recent_searches";
const MAX_RECENT_SEARCHES = 5;

function matchScore(text, query) {
  if (!text) return 0;
  const t = text.toLowerCase();
  const q = query.toLowerCase();
  if (t === q) return 100;
  if (t.startsWith(q)) return 80;
  if (t.includes(q)) return 60;
  // Match individual words
  const words = q.split(/\s+/);
  const matched = words.filter(w => t.includes(w));
  if (matched.length === words.length) return 50;
  if (matched.length > 0) return 30 * (matched.length / words.length);
  return 0;
}

function searchItems(items, query, fields, mapFn): SearchResult[] {
  if (!query || !items?.length) return [];
  const results: SearchResult[] = [];
  for (const item of items) {
    let best = 0;
    for (const field of fields) {
      const val = typeof field === "function" ? field(item) : item[field];
      const score = matchScore(String(val || ""), query);
      if (score > best) best = score;
    }
    if (best > 0) {
      results.push({ ...mapFn(item), score: best });
    }
  }
  results.sort((a, b) => b.score - a.score);
  return results.slice(0, MAX_RESULTS_PER_CATEGORY);
}

function loadRecentSearches() {
  try {
    const raw = localStorage.getItem(RECENT_SEARCHES_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveRecentSearch(query) {
  try {
    const recent = loadRecentSearches().filter(q => q !== query);
    recent.unshift(query);
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(recent.slice(0, MAX_RECENT_SEARCHES)));
  } catch { /* ignore */ }
}

function clearRecentSearches() {
  try { localStorage.removeItem(RECENT_SEARCHES_KEY); } catch { /* ignore */ }
}

export { NAVIGATION_SHORTCUTS };

export default function useGlobalSearch({ jobs, users, entities, currentUser, navigationShortcuts, extraResults }) {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Debounce search input by 150ms
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 150);
    return () => clearTimeout(timer);
  }, [query]);

  // Load recent searches when opening. Defer to a microtask so the setState
  // runs in a callback rather than synchronously in the effect body.
  useEffect(() => {
    if (!isOpen) return;
    Promise.resolve().then(() => setRecentSearches(loadRecentSearches()));
  }, [isOpen]);

  const role = currentUser?.role;
  const isAdmin = role === "admin";

  const { isEnabled } = useExperimental();
  const allShortcuts = navigationShortcuts || NAVIGATION_SHORTCUTS;
  // Shortcuts tied to an experimental feature only exist while it is on.
  const navShortcuts = useMemo(
    () => allShortcuts.filter((nav) => !nav.feature || isEnabled(nav.feature)),
    [allShortcuts, isEnabled]
  );

  const results = useMemo(() => {
    const q = debouncedQuery.trim();
    if (!q) return { navigation: [], officers: [], jobs: [], entities: [], extra: [], total: 0 };

    // Navigation shortcuts
    const navResults: SearchResult[] = [];
    for (const nav of navShortcuts) {
      if (nav.roles && !nav.roles.includes(role)) continue;
      const keywordScore = Math.max(...nav.keywords.map(k => matchScore(k, q)), 0);
      const labelScore = matchScore(nav.label, q);
      const best = Math.max(keywordScore, labelScore);
      if (best > 0) {
        navResults.push({ type: "navigation", label: nav.label, path: nav.path, score: best });
      }
    }
    navResults.sort((a, b) => b.score - a.score);

    // Officers — admin only
    const officerResults = isAdmin
      ? searchItems(users, q, ["name", "badge", "email"], (u) => ({
          type: "officer",
          id: u.id,
          label: u.name || u.email,
          sublabel: [u.badge ? `Badge: ${u.badge}` : null, u.email].filter(Boolean).join(" \u00B7 "),
          preview: [u.role !== "officer" ? u.role : null, u.phone].filter(Boolean).join(" \u00B7 ") || null,
          status: u.status,
          path: "/officers",
        }))
      : [];

    // Jobs — all logged-in users
    const jobResults = searchItems(
      jobs,
      q,
      ["title", "entity", "location", (j) => {
        if (!j.signups?.length) return "";
        return j.signups
          .map(uid => users.find(u => u.id === uid)?.name || "")
          .join(" ");
      }],
      (j) => ({
        type: "job",
        id: j.id,
        label: j.title,
        sublabel: [j.entity, j.date, j.location].filter(Boolean).join(" \u00B7 "),
        preview: `${(j.signups || []).length}/${j.slotsTotal} officers \u00B7 $${j.payRate}/hr`,
        status: j.status,
        path: `/jobs/${j.id}`,
      })
    );

    // Entities — admin only
    const entityResults = isAdmin
      ? searchItems(entities, q, ["name", "pointOfContact", "email", "phone"], (e) => ({
          type: "entity",
          id: e.id,
          label: e.name,
          sublabel: [e.pointOfContact, e.email].filter(Boolean).join(" \u00B7 "),
          status: e.status,
          path: "/entities-hub/manage",
        }))
      : [];

    // Extra results from caller (e.g. entity portal agencies)
    const extra = extraResults ? extraResults(q, matchScore) : [];

    const total = navResults.length + officerResults.length + jobResults.length + entityResults.length + extra.length;

    return {
      navigation: navResults.slice(0, MAX_RESULTS_PER_CATEGORY),
      officers: officerResults,
      jobs: jobResults,
      entities: entityResults,
      extra: extra.slice(0, MAX_RESULTS_PER_CATEGORY),
      total,
    };
  }, [debouncedQuery, jobs, users, entities, role, isAdmin, navShortcuts, extraResults]);

  const flatResults = useMemo(() => {
    return [
      ...results.navigation,
      ...results.officers,
      ...results.jobs,
      ...results.entities,
      ...results.extra,
    ];
  }, [results]);

  // Reset selected index when results change. Defer to a microtask so the
  // setState runs in a callback rather than synchronously in the effect body.
  useEffect(() => {
    Promise.resolve().then(() => setSelectedIndex(0));
  }, [flatResults.length, debouncedQuery]);

  const open = useCallback(() => {
    setIsOpen(true);
    setQuery("");
    setDebouncedQuery("");
    setSelectedIndex(0);
    setTimeout(() => inputRef.current?.focus(), 0);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setQuery("");
    setDebouncedQuery("");
    setSelectedIndex(0);
  }, []);

  const recordSearch = useCallback((q) => {
    const trimmed = q.trim();
    if (trimmed.length >= 2) {
      saveRecentSearch(trimmed);
    }
  }, []);

  const clearRecent = useCallback(() => {
    clearRecentSearches();
    setRecentSearches([]);
  }, []);

  const handleKeyDown = useCallback((e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex(i => Math.min(i + 1, flatResults.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === "Escape") {
      close();
    }
  }, [flatResults.length, close]);

  return {
    query, setQuery, debouncedQuery,
    isOpen, open, close,
    results, flatResults,
    selectedIndex, setSelectedIndex,
    handleKeyDown,
    inputRef,
    recentSearches, recordSearch, clearRecent,
  };
}
